import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    TrendingUp,
    TrendingDown,
    RefreshCw,
    Settings,
    Activity,
    Loader2,
    Wifi,
    WifiOff,
} from 'lucide-react';
import { useSnackbar } from 'notistack';
import { useCrmAllPrices } from '@/hooks/useCrmAllPrices';
import { useCrmInstruments } from '@/api/instruments/use-instruments';
import { useNavigate } from 'react-router-dom';
import { formatInstrumentPrice } from '@/utils/formatters';
import PriceDisplayWithFlash from '@/components/shared/PriceDisplayWithFlash';

interface RealTimePriceWidgetProps {
    selectedInstruments?: string[]; // Instrument IDs to monitor
    maxInstruments?: number;
    className?: string;
    showActions?: boolean;
    refreshInterval?: number; // Refresh interval in milliseconds
}

const RealTimePriceWidget: React.FC<RealTimePriceWidgetProps> = ({
    selectedInstruments = [],
    maxInstruments = 5,
    className,
    showActions = true,
    refreshInterval = 5000, // 5 seconds
}) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [isConnected, setIsConnected] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(new Date());
    const [priceHistory, setPriceHistory] = useState<Record<string, number[]>>({});

    const { data: instrumentsResponse } = useCrmInstruments();
    const instruments = instrumentsResponse?.data || [];

    // Get prices for selected instruments or popular instruments
    const instrumentsToMonitor =
        selectedInstruments.length > 0
            ? selectedInstruments.slice(0, maxInstruments)
            : instruments.slice(0, maxInstruments).map(inst => inst.id);

    const { prices: pricesData, isLoading, error, refetch } = useCrmAllPrices(instrumentsToMonitor);

    const prices = pricesData ? Object.fromEntries(pricesData) : {};

    // Track price history for trend analysis
    useEffect(() => {
        if (prices && Object.keys(prices).length > 0) {
            setPriceHistory(prev => {
                const newHistory = { ...prev };
                Object.entries(prices).forEach(([instrumentId, priceData]) => {
                    if (priceData && typeof priceData === 'object' && 'last' in priceData) {
                        const currentPrice = priceData.last;
                        if (!newHistory[instrumentId]) {
                            newHistory[instrumentId] = [];
                        }
                        newHistory[instrumentId].push(currentPrice);
                        // Keep only last 10 prices for trend analysis
                        if (newHistory[instrumentId].length > 10) {
                            newHistory[instrumentId] = newHistory[instrumentId].slice(-10);
                        }
                    }
                });
                return newHistory;
            });
            setLastUpdate(new Date());
            setIsConnected(true);
        }
    }, [prices]);

    // Handle connection status
    useEffect(() => {
        if (error) {
            setIsConnected(false);
        }
    }, [error]);

    const getPriceChange = (instrumentId: string) => {
        const history = priceHistory[instrumentId];
        if (!history || history.length < 2) return 0;

        const current = history[history.length - 1];
        const previous = history[history.length - 2];
        return ((current - previous) / previous) * 100;
    };

    const getPriceTrend = (instrumentId: string) => {
        const history = priceHistory[instrumentId];
        if (!history || history.length < 3) return 'neutral';

        const recent = history.slice(-3);
        const isIncreasing = recent.every(
            (price, index) => index === 0 || price >= recent[index - 1]
        );
        const isDecreasing = recent.every(
            (price, index) => index === 0 || price <= recent[index - 1]
        );

        if (isIncreasing) return 'up';
        if (isDecreasing) return 'down';
        return 'neutral';
    };

    const formatPrice = (price: number, instrumentId: string) => {
        const instrument = instruments.find(inst => inst.id === instrumentId);
        return formatInstrumentPrice(price, instrument);
    };

    const formatChange = (change: number) => {
        const sign = change >= 0 ? '+' : '';
        return `${sign}${change.toFixed(2)}%`;
    };

    const handleRefresh = () => {
        refetch();
        enqueueSnackbar('Prices refreshed', { variant: 'success' });
    };

    const handleViewTrading = () => {
        navigate('/trade');
    };

    const getInstrumentSymbol = (instrumentId: string) => {
        const instrument = instruments.find(inst => inst.id === instrumentId);
        return instrument?.symbol || instrumentId;
    };

    const getInstrumentName = (instrumentId: string) => {
        const instrument = instruments.find(inst => inst.id === instrumentId);
        return instrument?.symbol || instrumentId;
    };

    return (
        <Card className={className}>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <Activity className="h-5 w-5" />
                        Real-Time Prices
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                            {isConnected ? (
                                <Wifi className="h-4 w-4 text-green-500" />
                            ) : (
                                <WifiOff className="h-4 w-4 text-red-500" />
                            )}
                            <span className="text-xs text-muted-foreground">
                                {isConnected ? 'Live' : 'Offline'}
                            </span>
                        </div>
                        {showActions && (
                            <>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleRefresh}
                                    disabled={isLoading}
                                    className="h-8 w-8 p-0"
                                >
                                    <RefreshCw
                                        className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                                    />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleViewTrading}
                                    className="h-8 w-8 p-0"
                                >
                                    <Settings className="h-4 w-4" />
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {isLoading && !prices ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Connection Status */}
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                                Last update: {lastUpdate.toLocaleTimeString()}
                            </span>
                            <Badge variant={isConnected ? 'default' : 'destructive'}>
                                {isConnected ? 'Connected' : 'Disconnected'}
                            </Badge>
                        </div>

                        {/* Price List */}
                        <div className="space-y-3">
                            {instrumentsToMonitor.map(instrumentId => {
                                const priceData = prices[instrumentId];
                                const currentPrice = priceData?.last || 0;
                                const change = getPriceChange(instrumentId);
                                const trend = getPriceTrend(instrumentId);
                                const symbol = getInstrumentSymbol(instrumentId);
                                const name = getInstrumentName(instrumentId);

                                return (
                                    <div
                                        key={instrumentId}
                                        className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-2">
                                                {trend === 'up' && (
                                                    <TrendingUp className="h-4 w-4 text-green-500" />
                                                )}
                                                {trend === 'down' && (
                                                    <TrendingDown className="h-4 w-4 text-red-500" />
                                                )}
                                                {trend === 'neutral' && (
                                                    <div className="h-4 w-4 rounded-full bg-gray-400" />
                                                )}
                                            </div>
                                            <div>
                                                <div className="font-semibold text-sm">
                                                    {symbol}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {name}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <PriceDisplayWithFlash
                                                price={currentPrice}
                                                className="font-mono text-sm font-bold"
                                                formatPrice={p => formatPrice(p, instrumentId)}
                                            />
                                            <div
                                                className={`text-xs font-medium ${
                                                    change >= 0 ? 'text-green-600' : 'text-red-600'
                                                }`}
                                            >
                                                {formatChange(change)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* No Data State */}
                        {Object.keys(prices).length === 0 && !isLoading && (
                            <div className="text-center text-muted-foreground py-8">
                                <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No price data available</p>
                                <p className="text-xs">Select instruments to monitor</p>
                            </div>
                        )}

                        {/* Error State */}
                        {error && (
                            <div className="text-center text-red-500 py-4">
                                <WifiOff className="h-8 w-8 mx-auto mb-2" />
                                <p className="text-sm">Failed to fetch prices</p>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleRefresh}
                                    className="mt-2"
                                >
                                    Retry
                                </Button>
                            </div>
                        )}

                        {/* Refresh Info */}
                        <div className="text-center text-xs text-muted-foreground">
                            Updates every {refreshInterval / 1000}s
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default RealTimePriceWidget;
